/**
 * AlertsPanel.jsx — Multi-tier alert settings panel
 *
 * Three tabs: Monitor | Tiers | Log
 * Tiers tab has a sub-tab per tier (Standard / Confirmed / High Conviction).
 * Each tier has fully independent settings on one scrollable settings page.
 */

import { useState, useRef, useCallback } from 'react';
import { TIER_IDS, TIER_DEFAULTS, TIER_META } from '../../lib/alertTiers';
import { useMarketOpen } from '../../lib/marketHours';
import VolatilityPanel from './VolatilityPanel';
import FirstDecisionGate from './FirstDecisionGate';
import QuickMonitorTab   from './QuickMonitorTab';
import SafeDayTab        from './SafeDayTab';
import GameDayTab        from './GameDayTab';
import AdvicePanel       from './AdvicePanel';
import { classifySessionRegime, suggestedSizeMultiplier } from '../../lib/sessionRegime';
import {
  tierPutRingtone, tierDeleteRingtone, tierTestPush, alertTestPush,
} from '../../lib/api';

// ── Shared audio helpers ──────────────────────────────────────────────────────

function playBeep(severity='bullish', volume=0.7) {
  try {
    const ctx=new(window.AudioContext||window.webkitAudioContext)();
    const osc=ctx.createOscillator(); const gain=ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination); osc.type='sine';
    osc.frequency.setValueAtTime(severity==='bullish'?880:440,ctx.currentTime);
    gain.gain.setValueAtTime(volume,ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+0.45);
    osc.start(); osc.stop(ctx.currentTime+0.45);
  } catch {}
}

// ── Signal metadata ───────────────────────────────────────────────────────────

const SIGNAL_META = {
  oversold:   { label:'RSI Oversold',        icon:'↘', dir:'bullish' },
  overbought: { label:'RSI Overbought',       icon:'↗', dir:'bearish' },
  bbLower:    { label:'BB Lower Breach',      icon:'⤓', dir:'bullish' },
  bbUpper:    { label:'BB Upper Breach',      icon:'⤒', dir:'bearish' },
};

const PRIORITY_OPTS = [
  { value:-2, label:'-2 — Silent' },
  { value:-1, label:'-1 — Quiet' },
  { value: 0, label: '0 — Normal' },
  { value: 1, label: '1 — High (bypass quiet hours)' },
  { value: 2, label: '2 — Emergency (repeats)' },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function fmtTime(ts) {
  return new Date(ts).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false});
}
function fmtDate(ts) {
  return new Date(ts).toLocaleDateString('en-US',{month:'short',day:'numeric'});
}

const sCard = { padding:'13px 15px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-hover)', marginBottom:13 };
const inputSt = {
  width:'100%', boxSizing:'border-box', padding:'6px 9px', borderRadius:4,
  border:'1px solid var(--border)', background:'var(--bg)', color:'var(--text)', fontSize:12,
};
const btnSt = {
  padding:'5px 11px', borderRadius:4, fontSize:11, fontWeight:600, cursor:'pointer',
  border:'1px solid var(--border)', background:'var(--bg-hover)', color:'var(--muted)',
};

function Slider({ label, value, min, max, step, unit='', onChange, note }) {
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ fontSize:11, color:'var(--muted)', marginBottom:2 }}>
        {label}: <strong style={{ color:'var(--text)', fontFamily:'monospace' }}>{value}{unit}</strong>
        {note && <span style={{ color:'var(--faint)', marginLeft:6 }}>{note}</span>}
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e=>onChange(Number(e.target.value))} style={{ width:'100%' }} />
    </div>
  );
}

function LogRow({ entry, onDelete, tiers }) {
  const [open, setOpen] = useState(false);
  const isUp = entry.severity==='bullish';
  const meta = TIER_META[entry.tierId];
  return (
    <div onClick={()=>setOpen(o=>!o)} style={{
      padding:'7px 10px', marginBottom:4, cursor:'pointer',
      background:'var(--bg)', borderRadius:'0 4px 4px 0',
      border:'1px solid var(--border)', borderLeft:`2px solid ${isUp?'var(--up)':'var(--down)'}`,
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', gap:7, alignItems:'center' }}>
          {meta && (
            <span style={{
              fontSize:9, fontWeight:700, padding:'1px 5px', borderRadius:3,
              background:`${meta.color}20`, color:meta.color, border:`1px solid ${meta.color}`,
              letterSpacing:'.05em',
            }}>{meta.name.toUpperCase()}</span>
          )}
          <span style={{ fontSize:9, fontWeight:700, padding:'1px 5px', borderRadius:3, letterSpacing:'.06em',
            background:isUp?'var(--up-soft)':'var(--down-soft)', color:isUp?'var(--up)':'var(--down)' }}>
            {isUp?'BULL':'BEAR'}
          </span>
          {entry.symbol && (
            <span style={{ fontSize:10, fontFamily:'monospace', fontWeight:700, color:'var(--text)' }}>
              {entry.symbol}
            </span>
          )}
          <span style={{ fontSize:12, fontWeight:600 }}>{entry.label}</span>
        </div>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          {entry.confluenceCount>1 && (
            <span style={{ fontSize:9, fontFamily:'monospace', color:'var(--faint)' }}>
              {entry.confluenceCount} signals
            </span>
          )}
          <span style={{ fontSize:10, fontFamily:'monospace', color:'var(--faint)' }}>
            {fmtDate(entry.timestamp)} {fmtTime(entry.timestamp)}
          </span>
          <button onClick={e=>{e.stopPropagation();onDelete(entry.id);}}
            style={{ background:'none',border:'none',cursor:'pointer',color:'var(--faint)',fontSize:14,lineHeight:1,padding:'0 2px' }}>×</button>
        </div>
      </div>
      {open && (
        <div style={{ marginTop:6, fontSize:11, color:'var(--muted)', fontFamily:'monospace', lineHeight:1.6 }}>
          <div>{entry.detail}</div>
          {entry.price && <span style={{ marginRight:12 }}>SPY ${entry.price.toFixed(2)}</span>}
          {entry.rsi   && <span>RSI {entry.rsi.toFixed(1)}</span>}
          {entry.activeSignals?.length > 0 && (
            <div style={{ marginTop:3, color:'var(--faint)' }}>Active: {entry.activeSignals.join(' + ')}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tier settings pane ────────────────────────────────────────────────────────

function TierSettingsPane({ tierId, tierState, globalSettings, onSave, onSetRingtone }) {
  const meta      = TIER_META[tierId];
  const defaults  = TIER_DEFAULTS[tierId];
  const [draft,   setDraft]   = useState(() => ({ ...defaults, ...tierState.settings }));
  const [dirty,   setDirty]   = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [testing, setTesting] = useState(null); // null | 'sending' | 'ok' | 'err'
  const [testErr, setTestErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const [notifPerm, setNotifPerm] = useState(()=>typeof Notification!=='undefined'?Notification.permission:'default');
  const fileRef = useRef(null);

  // Sync if tierState changes externally (after initial load from KV)
  const [prev, setPrev] = useState(tierState.settings);
  if (tierState.settings !== prev) {
    setPrev(tierState.settings);
    setDraft({ ...defaults, ...tierState.settings });
    setDirty(false);
  }

  const upd = (k, v) => { setDraft(d=>({...d,[k]:v})); setDirty(true); };
  const toggleSignal = (key) => {
    const curr = draft.enabledSignals??[];
    upd('enabledSignals', curr.includes(key)?curr.filter(k=>k!==key):[...curr,key]);
  };

  const doSave = async () => {
    setSaving(true);
    try { await onSave(tierId, draft); setDirty(false); }
    catch(e) { alert('Save failed: '+e.message); }
    finally { setSaving(false); }
  };

  const handleFile = async (file) => {
    if (!file||!file.type.startsWith('audio/')) { alert('Select an audio file'); return; }
    if (file.size>3_500_000) { alert('File must be under 3.5MB'); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res,rej)=>{
        const r=new FileReader(); r.onload=()=>res(r.result); r.onerror=rej; r.readAsDataURL(file);
      });
      await tierPutRingtone(tierId, dataUrl);
      onSetRingtone(tierId, dataUrl, file.name);
    } catch(e) { alert('Upload failed: '+e.message); }
    finally { setUploading(false); }
  };

  const deleteRingtone = async () => {
    await tierDeleteRingtone(tierId);
    onSetRingtone(tierId, null, '');
  };

  const testAudio = () => {
    if (tierState.ringtoneUrl) {
      const a = new Audio(tierState.ringtoneUrl);
      a.volume = draft.audioVolume??0.7; a.play().catch(()=>{});
      if ((draft.ringtoneCutoffSecs??0)>0) setTimeout(()=>{ try{a.pause();}catch{} }, (draft.ringtoneCutoffSecs)*1000);
    } else {
      playBeep('bullish', draft.audioVolume??0.7);
    }
  };

  const sendTestPush = async () => {
    setTesting('sending'); setTestErr('');
    try {
      const r = await tierTestPush(tierId);
      if (r.ok) { setTesting('ok'); setTimeout(()=>setTesting(null),3000); }
      else { setTesting('err'); setTestErr(r.pushover?.errors?.join(', ')||'Check credentials'); }
    } catch(e) { setTesting('err'); setTestErr(e.message); }
  };

  const requestNotif = async () => {
    const result = await Notification.requestPermission();
    setNotifPerm(result);
    if (result==='granted') upd('browserNotif', true);
  };

  const hasCredentials = !!(
    (draft.pushoverTokenOverride||globalSettings.pushoverToken) &&
    (draft.pushoverUserOverride||globalSettings.pushoverUser)
  );

  return (
    <div style={{ padding:'2px 0 60px' }}>

      {/* ── Enable + Auto-enable ────────────────────────────────────────── */}
      <div style={{ ...sCard, display:'flex', gap:20, alignItems:'center', flexWrap:'wrap' }}>
        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600 }}>
          <input type="checkbox" checked={draft.enabled??true} onChange={e=>upd('enabled',e.target.checked)} />
          <span style={{ color:'var(--text)' }}>Tier enabled</span>
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12 }}>
          <input type="checkbox" checked={draft.autoEnableOnOpen??false} onChange={e=>upd('autoEnableOnOpen',e.target.checked)} />
          <div>
            <div style={{ color:'var(--text)', fontWeight:500 }}>Auto-enable on market open</div>
            <div style={{ fontSize:10, color:'var(--faint)' }}>Starts the engine at 9:30 ET automatically</div>
          </div>
        </label>
      </div>

      {/* ── Active signals ──────────────────────────────────────────────── */}
      <div style={sCard}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--muted)', marginBottom:10 }}>Active Signals</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
          {Object.entries(SIGNAL_META).map(([key,meta])=>{
            const on=(draft.enabledSignals??[]).includes(key);
            return (
              <label key={key} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12 }}>
                <input type="checkbox" checked={on} onChange={()=>toggleSignal(key)} />
                <span style={{ color:on?'var(--text)':'var(--faint)' }}>{meta.icon} {meta.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* ── Thresholds ──────────────────────────────────────────────────── */}
      <div style={sCard}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--muted)', marginBottom:12 }}>Signal Thresholds</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 20px' }}>
          <Slider label="RSI Oversold ≤"   value={draft.rsiOversold??30}   min={10} max={45}  step={1}   onChange={v=>upd('rsiOversold',v)} />
          <Slider label="RSI Overbought ≥" value={draft.rsiOverbought??70} min={55} max={90}  step={1}   onChange={v=>upd('rsiOverbought',v)} />
          <Slider label="Bars to persist"  value={draft.persistBars??3}    min={1}  max={10}  step={1}   onChange={v=>upd('persistBars',v)} />
          <Slider label="BB sigma"         value={draft.bbSigma??1}        min={0.5}max={2.5} step={0.5} onChange={v=>upd('bbSigma',v)} />
        </div>
      </div>

      {/* ── Confluence requirements ──────────────────────────────────────── */}
      <div style={sCard}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--muted)', marginBottom:10 }}>Confluence Requirements</div>
        <div style={{ fontSize:11, color:'var(--faint)', marginBottom:12, lineHeight:1.5 }}>
          How many independent signals must agree before this tier fires. RSI, BB breach, and VWAP deviation each count as one.
        </div>
        <Slider label="Signals required (min confluence)" value={draft.minConfluence??1} min={1} max={3} step={1} onChange={v=>upd('minConfluence',v)}
          note={draft.minConfluence===1?'— any single signal fires':draft.minConfluence===2?'— 2 signals must agree':'— all 3 must agree (rare)'} />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 20px', marginTop:8 }}>
          <label style={{ display:'flex', alignItems:'flex-start', gap:8, cursor:'pointer', fontSize:12 }}>
            <input type="checkbox" checked={draft.requireBBbreach??false} onChange={e=>upd('requireBBbreach',e.target.checked)} style={{ marginTop:2 }} />
            <div>
              <div style={{ color:'var(--text)' }}>Require BB breach</div>
              <div style={{ fontSize:10, color:'var(--faint)' }}>Price must be outside BB band</div>
            </div>
          </label>
          <label style={{ display:'flex', alignItems:'flex-start', gap:8, cursor:'pointer', fontSize:12 }}>
            <input type="checkbox" checked={draft.requireVWAPdev??false} onChange={e=>upd('requireVWAPdev',e.target.checked)} style={{ marginTop:2 }} />
            <div>
              <div style={{ color:'var(--text)' }}>Require VWAP deviation</div>
              <div style={{ fontSize:10, color:'var(--faint)' }}>Price must be ≥ threshold % from VWAP</div>
            </div>
          </label>
        </div>
        {(draft.requireVWAPdev||draft.minConfluence>=2) && (
          <div style={{ marginTop:10 }}>
            <Slider label="VWAP deviation threshold %" value={draft.vwapDeviationPct??0.2} min={0.1} max={1} step={0.05} onChange={v=>upd('vwapDeviationPct',v)} />
          </div>
        )}
      </div>

      {/* ── Audio ───────────────────────────────────────────────────────── */}
      <div style={sCard}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--muted)' }}>Audio</div>
          <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:12 }}>
            <input type="checkbox" checked={draft.audioEnabled??true} onChange={e=>upd('audioEnabled',e.target.checked)} />
            Enabled
          </label>
        </div>

        <Slider label="Volume" value={Math.round((draft.audioVolume??0.7)*100)} unit="%" min={0} max={100} step={5}
          onChange={v=>upd('audioVolume',v/100)} />
        <Slider label="Alert cooldown" value={draft.cooldownMins??10} unit=" min" min={1} max={60} step={1}
          onChange={v=>upd('cooldownMins',v)} note="— min gap between same signal" />
        <Slider label="Ringtone cutoff" value={draft.ringtoneCutoffSecs??0} min={0} max={30} step={1}
          onChange={v=>upd('ringtoneCutoffSecs',v)}
          note={draft.ringtoneCutoffSecs===0?'— plays full clip':`— stops after ${draft.ringtoneCutoffSecs}s`} />

        <label style={{ display:'flex', alignItems:'flex-start', gap:8, cursor:'pointer', fontSize:12, marginBottom:12 }}>
          <input type="checkbox" checked={draft.gradualVolume??false} onChange={e=>upd('gradualVolume',e.target.checked)} style={{ marginTop:2 }} />
          <div>
            <div style={{ color:'var(--text)', fontWeight:500 }}>Gradually increase volume</div>
            <div style={{ fontSize:10, color:'var(--faint)', marginTop:2 }}>
              Ramps 30% → 130% of volume over the cutoff window.
              {(draft.ringtoneCutoffSecs??0)===0 && (draft.gradualVolume??false) && (
                <span style={{ color:'var(--warn)', marginLeft:4 }}>Set a cutoff time to enable.</span>
              )}
            </div>
          </div>
        </label>

        {/* Ringtone */}
        <div style={{ borderTop:'1px solid var(--border)', paddingTop:12 }}>
          <div style={{ fontSize:11, fontWeight:600, color:'var(--muted)', marginBottom:6 }}>Ringtone</div>
          <div style={{ fontSize:11, color:'var(--faint)', marginBottom:10 }}>
            Per-tier audio file (mp3/wav/ogg, max 3.5MB). Saved to account — loads on any device.
          </div>
          {tierState.ringtoneUrl ? (
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
              <span style={{ fontSize:11, padding:'3px 8px', borderRadius:4, background:'var(--up-soft)', color:'var(--up)', fontWeight:600 }}>
                🎵 {tierState.ringtoneName||'Custom tone'}
              </span>
              <button onClick={testAudio} style={btnSt}>▶ Test</button>
              <label style={{ ...btnSt, display:'inline-block', cursor:'pointer' }}>
                Replace
                <input type="file" accept="audio/*" style={{ display:'none' }}
                  onChange={e=>{ if(e.target.files[0]) handleFile(e.target.files[0]); e.target.value=''; }} />
              </label>
              <button onClick={deleteRingtone} style={{ ...btnSt, color:'var(--down)' }}>Remove</button>
            </div>
          ) : (
            <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
              <label style={{ ...btnSt, display:'inline-block', cursor:'pointer', opacity:uploading?0.6:1 }}>
                {uploading?'Uploading…':'↑ Upload audio'}
                <input type="file" accept="audio/*" style={{ display:'none' }}
                  onChange={e=>{ if(e.target.files[0]) handleFile(e.target.files[0]); e.target.value=''; }} />
              </label>
              <button onClick={testAudio} style={btnSt}>▶ Test beep</button>
              <span style={{ fontSize:11, color:'var(--faint)' }}>falls back to built-in beep</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Browser notifications ────────────────────────────────────────── */}
      <div style={sCard}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--muted)', marginBottom:8 }}>Browser Notifications</div>
        <div style={{ fontSize:11, color:'var(--faint)', marginBottom:10 }}>Desktop pop-up even when tab is backgrounded.</div>
        {notifPerm==='granted' ? (
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12 }}>
            <input type="checkbox" checked={draft.browserNotif??false} onChange={e=>upd('browserNotif',e.target.checked)} />
            Send notification on this tier's alerts
          </label>
        ) : notifPerm==='denied' ? (
          <div style={{ fontSize:11, color:'var(--down)' }}>Blocked — reset in browser site settings.</div>
        ) : (
          <button onClick={requestNotif} style={btnSt}>Enable browser notifications</button>
        )}
      </div>

      {/* ── Pushover ─────────────────────────────────────────────────────── */}
      <div style={sCard}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--muted)' }}>Mobile Push · Pushover</div>
          <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:12 }}>
            <input type="checkbox" checked={draft.pushoverEnabled??true} onChange={e=>upd('pushoverEnabled',e.target.checked)} />
            Enabled
          </label>
        </div>
        <div style={{ fontSize:11, color:'var(--faint)', marginBottom:12, lineHeight:1.5 }}>
          Uses global credentials by default. Enter overrides below to use different Pushover apps per tier.
          Global credentials are set in the <strong style={{ color:'var(--muted)' }}>Global</strong> tab.
        </div>

        {/* Priority */}
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:6 }}>Priority</div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {PRIORITY_OPTS.map(opt=>(
              <label key={opt.value} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12 }}>
                <input type="radio" name={`prio-${tierId}`}
                  checked={(draft.pushoverPriority??1)===opt.value}
                  onChange={()=>upd('pushoverPriority',opt.value)} />
                <span style={{ fontFamily:'monospace', fontSize:11, color:'var(--text)' }}>{opt.label}</span>
              </label>
            ))}
          </div>
          {(draft.pushoverPriority??1)===2 && (
            <div style={{ marginTop:8, fontSize:11, color:'var(--warn)', padding:'6px 10px', borderRadius:4,
              background:'rgba(234,179,8,0.08)', border:'1px solid var(--warn)' }}>
              Emergency repeats every 60 seconds until acknowledged on your phone.
            </div>
          )}
        </div>

        {/* Credential overrides */}
        <div style={{ borderTop:'1px solid var(--border)', paddingTop:12, marginBottom:10 }}>
          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:8 }}>Credential overrides (optional)</div>
          <div style={{ display:'grid', gap:8 }}>
            <div>
              <div style={{ fontSize:11, color:'var(--faint)', marginBottom:4 }}>App Token override</div>
              <input type="password" placeholder="Leave blank to use global token" style={inputSt}
                value={draft.pushoverTokenOverride||''} onChange={e=>upd('pushoverTokenOverride',e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize:11, color:'var(--faint)', marginBottom:4 }}>User Key override</div>
              <input type="password" placeholder="Leave blank to use global user key" style={inputSt}
                value={draft.pushoverUserOverride||''} onChange={e=>upd('pushoverUserOverride',e.target.value)} />
            </div>
          </div>
        </div>

        {/* Test push */}
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <button onClick={sendTestPush} disabled={testing==='sending'||!hasCredentials}
            style={{ ...btnSt, opacity:!hasCredentials?0.4:1,
              borderColor:testing==='ok'?'var(--up)':testing==='err'?'var(--down)':'var(--border)',
              color:testing==='ok'?'var(--up)':testing==='err'?'var(--down)':'var(--muted)',
            }}>
            {testing==='sending'?'⟳ Sending…':testing==='ok'?'✓ Sent!':'📱 Send test push'}
          </button>
          {testing==='err' && <span style={{ fontSize:11, color:'var(--down)' }}>{testErr}</span>}
          {!hasCredentials && <span style={{ fontSize:11, color:'var(--faint)' }}>Add credentials in Global tab</span>}
        </div>
      </div>

      {/* ── Sticky save bar ──────────────────────────────────────────────── */}
      {dirty && (
        <div style={{
          position:'sticky', bottom:16, display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'10px 14px', borderRadius:6, border:'1px solid var(--border)',
          background:'var(--bg)', boxShadow:'0 4px 16px rgba(0,0,0,.3)',
        }}>
          <span style={{ fontSize:12, color:'var(--muted)' }}>Unsaved changes</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>{ setDraft({ ...defaults, ...tierState.settings }); setDirty(false); }} style={btnSt}>Discard</button>
            <button onClick={doSave} disabled={saving} style={{ ...btnSt, background:'var(--accent)', color:'var(--bg)', borderColor:'var(--accent)', fontWeight:700 }}>
              {saving?'Saving…':'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Global credentials + symbol list tab ─────────────────────────────────────

function GlobalPane({ globalSettings, onSave }) {
  const [draft,  setDraft]  = useState(globalSettings);
  const [dirty,  setDirty]  = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testErr, setTestErr] = useState('');
  const [notifPerm, setNotifPerm] = useState(()=>typeof Notification!=='undefined'?Notification.permission:'default');
  const [newSym, setNewSym] = useState('');

  const prev = useRef(globalSettings);
  if (globalSettings !== prev.current) {
    prev.current = globalSettings;
    setDraft(globalSettings);
    setDirty(false);
  }

  const upd = (k,v)=>{ setDraft(d=>({...d,[k]:v})); setDirty(true); };

  const symbols = draft.watchedSymbols ?? ['SPY'];

  const addSymbol = () => {
    const s = newSym.trim().toUpperCase();
    if (!s || symbols.includes(s)) { setNewSym(''); return; }
    upd('watchedSymbols', [...symbols, s]);
    setNewSym('');
  };

  const removeSymbol = (sym) => {
    if (symbols.length <= 1) return; // always keep at least one
    upd('watchedSymbols', symbols.filter(s => s !== sym));
  };

  const doSave = async () => {
    setSaving(true);
    try { await onSave(draft); setDirty(false); }
    catch(e) { alert('Save failed: '+e.message); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting('sending'); setTestErr('');
    try {
      const r = await alertTestPush();
      if (r.ok) { setTesting('ok'); setTimeout(()=>setTesting(null),3000); }
      else { setTesting('err'); setTestErr(r.pushover?.errors?.join(', ')||'Check credentials'); }
    } catch(e) { setTesting('err'); setTestErr(e.message); }
  };

  const requestNotif = async () => {
    const result = await Notification.requestPermission();
    setNotifPerm(result);
    if (result==='granted') upd('browserNotif',true);
  };

  return (
    <div style={{ padding:'2px 0 60px' }}>

      {/* ── Watched symbols ──────────────────────────────────────────────── */}
      <div style={sCard}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--muted)', marginBottom:6 }}>Watched Symbols</div>
        <div style={{ fontSize:11, color:'var(--faint)', marginBottom:12, lineHeight:1.5 }}>
          Each symbol gets its own independent signal engine using the same tier settings.
          Add any Yahoo Finance ticker (NVDA, QQQ, TSLA, etc.).
        </div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
          {symbols.map(sym => (
            <div key={sym} style={{
              display:'flex', alignItems:'center', gap:5,
              padding:'4px 10px', borderRadius:20, fontFamily:'monospace',
              fontSize:12, fontWeight:700,
              background:'var(--bg)', border:'1px solid var(--accent)',
              color:'var(--accent)',
            }}>
              {sym}
              {symbols.length > 1 && (
                <button onClick={()=>removeSymbol(sym)} style={{
                  background:'none', border:'none', cursor:'pointer',
                  color:'var(--faint)', fontSize:13, lineHeight:1, padding:0, marginLeft:2,
                }}>×</button>
              )}
            </div>
          ))}
        </div>
        <div style={{ display:'flex', gap:6 }}>
          <input
            value={newSym}
            onChange={e=>setNewSym(e.target.value.toUpperCase())}
            onKeyDown={e=>e.key==='Enter'&&addSymbol()}
            placeholder="e.g. NVDA"
            maxLength={10}
            style={{ ...inputSt, width:120, fontFamily:'monospace', fontWeight:700, textTransform:'uppercase' }}
          />
          <button onClick={addSymbol} style={btnSt}>+ Add</button>
        </div>
        <div style={{ marginTop:10, fontSize:10, color:'var(--faint)', lineHeight:1.5 }}>
          Tip: The alert engine polls Yahoo Finance every 60s per symbol during market hours.
          Each additional symbol costs one extra API call per minute. Keep the list focused.
        </div>
      </div>

      {/* ── Pushover credentials ─────────────────────────────────────────── */}
      <div style={sCard}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--muted)', marginBottom:10 }}>Pushover Credentials</div>
        <div style={{ fontSize:11, color:'var(--faint)', marginBottom:12 }}>
          <a href="https://pushover.net" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>pushover.net →</a>
          {' '}$5 one-time. Shared by all tiers unless a tier sets credential overrides.
        </div>
        <div style={{ display:'grid', gap:10 }}>
          <div>
            <div style={{ fontSize:11, color:'var(--muted)', marginBottom:4 }}>App Token</div>
            <input type="password" placeholder="aTokenXXX…" style={inputSt}
              value={draft.pushoverToken||''} onChange={e=>upd('pushoverToken',e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize:11, color:'var(--muted)', marginBottom:4 }}>User Key</div>
            <input type="password" placeholder="uUserXXX…" style={inputSt}
              value={draft.pushoverUser||''} onChange={e=>upd('pushoverUser',e.target.value)} />
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:12 }}>
          <button onClick={sendTest} disabled={testing==='sending'||!draft.pushoverToken||!draft.pushoverUser}
            style={{ ...btnSt, opacity:(!draft.pushoverToken||!draft.pushoverUser)?0.4:1,
              borderColor:testing==='ok'?'var(--up)':testing==='err'?'var(--down)':'var(--border)',
              color:testing==='ok'?'var(--up)':testing==='err'?'var(--down)':'var(--muted)',
            }}>
            {testing==='sending'?'⟳ Sending…':testing==='ok'?'✓ Sent!':'📱 Test push'}
          </button>
          {testing==='err' && <span style={{ fontSize:11, color:'var(--down)' }}>{testErr}</span>}
        </div>
      </div>

      {/* ── Browser notifications ─────────────────────────────────────────── */}
      <div style={sCard}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--muted)', marginBottom:8 }}>Browser Notifications</div>
        <div style={{ fontSize:11, color:'var(--faint)', marginBottom:10 }}>Global fallback — individual tiers can enable/disable independently.</div>
        {notifPerm==='granted' ? (
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12 }}>
            <input type="checkbox" checked={draft.browserNotif??false} onChange={e=>upd('browserNotif',e.target.checked)} />
            Enable browser notifications globally
          </label>
        ) : notifPerm==='denied' ? (
          <div style={{ fontSize:11, color:'var(--down)' }}>Blocked — reset in browser site settings.</div>
        ) : (
          <button onClick={requestNotif} style={btnSt}>Enable browser notifications</button>
        )}
      </div>

      {/* ── Session Guard settings ────────────────────────────────────── */}
      <div style={sCard}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--muted)', marginBottom:6 }}>Session Guard</div>
        <div style={{ fontSize:11, color:'var(--faint)', marginBottom:14, lineHeight:1.5 }}>
          A full-screen warning appears at your set review time. You must acknowledge it and enter your P&L to continue.
          If your loss exceeds the daily limit, the engine stops for the rest of the session.
        </div>
        <div style={{ display:'grid', gap:14 }}>
          <div>
            <div style={{ fontSize:11, color:'var(--muted)', marginBottom:4 }}>Review time (SGT, 24h — e.g. 15:30)</div>
            <input type="time" style={inputSt}
              value={draft.warningTime||''}
              onChange={e=>upd('warningTime',e.target.value)} />
            <div style={{ fontSize:10, color:'var(--faint)', marginTop:3 }}>Leave blank to disable the warning screen.</div>
          </div>
          <div>
            <div style={{ fontSize:11, color:'var(--muted)', marginBottom:4 }}>Warning message</div>
            <textarea rows={3} style={{ ...inputSt, resize:'vertical', fontFamily:'inherit' }}
              placeholder="You have reached your scheduled review time. Assess your session before continuing."
              value={draft.warningMessage||''}
              onChange={e=>upd('warningMessage',e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize:11, color:'var(--muted)', marginBottom:4 }}>Max daily loss ($) — session stops if exceeded</div>
            <input type="number" min={0} step={50} style={{ ...inputSt, maxWidth:140 }}
              placeholder="e.g. 500"
              value={draft.maxDailyLoss||''}
              onChange={e=>upd('maxDailyLoss', e.target.value==='' ? '' : Number(e.target.value))} />
            <div style={{ fontSize:10, color:'var(--faint)', marginTop:3 }}>Set to 0 or leave blank to disable the hard stop.</div>
          </div>
        </div>
      </div>

      {dirty && (
        <div style={{ position:'sticky', bottom:16, display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'10px 14px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg)', boxShadow:'0 4px 16px rgba(0,0,0,.3)' }}>
          <span style={{ fontSize:12, color:'var(--muted)' }}>Unsaved changes</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>{ setDraft(globalSettings); setDirty(false); }} style={btnSt}>Discard</button>
            <button onClick={doSave} disabled={saving} style={{ ...btnSt, background:'var(--accent)', color:'var(--bg)', borderColor:'var(--accent)', fontWeight:700 }}>
              {saving?'Saving…':'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
export default function AlertsPanel({
  engineOn, muted, liveState, symbolStates, symbolAlertCounts, alertCount, tierAlertCounts,
  tiers, globalSettings,
  log,
  start, stop, mute, unmute,
  saveTierSettings, saveGlobalSettings, setTierRingtone,
  clearLog, deleteLogEntry,
  vixQuote,
}) {
  const [tab,      setTab]      = useState(() => localStorage.getItem('od:alertsTab') || 'overview');
  const switchTab = (t) => { setTab(t); localStorage.setItem('od:alertsTab', t); };
  const [tierTab,  setTierTab]  = useState('tier1');
  const [regimeOpen,   setRegimeOpen]   = useState(true);  // Session Regime + Gate accordion
  const [expandedSyms, setExpandedSyms] = useState({});    // per-symbol indicator accordion
  const [gatePlan,     setGatePlan]     = useState('');    // lifted from FirstDecisionGate

  const marketOpen = useMarketOpen(); // updates every 30s, always accurate

  const persist   = liveState?.tierPersist ?? {};
  const alerting  = engineOn && TIER_IDS.some(id=>Object.values(persist[id]??{}).some(v=>v>0));

  const tabBtn = (key, label) => (
    <button onClick={()=>switchTab(key)} style={{
      padding:'5px 14px', border:'none', cursor:'pointer',
      fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase',
      background:tab===key?'var(--accent)':'transparent',
      color:tab===key?'var(--bg)':'var(--muted)',
      borderRadius:4, transition:'all .15s',
    }}>{label}</button>
  );

  const tierTabBtn = (id) => {
    const meta    = TIER_META[id];
    const count   = tierAlertCounts?.[id] ?? 0;
    const isActive= tab==='tiers' && tierTab===id;
    return (
      <button key={id} onClick={()=>setTierTab(id)} style={{
        padding:'5px 12px', border:`1px solid ${isActive?meta.color:'var(--border)'}`,
        cursor:'pointer', fontSize:11, fontWeight:700, letterSpacing:'.05em',
        background:isActive?`${meta.color}18`:'transparent',
        color:isActive?meta.color:'var(--muted)',
        borderRadius:4, transition:'all .15s', position:'relative',
      }}>
        <span style={{ fontFamily:'monospace', marginRight:5, fontSize:10, opacity:.7 }}>{meta.badge}</span>
        {meta.name}
        {count>0 && (
          <span style={{ marginLeft:5, fontSize:9, padding:'1px 4px', borderRadius:8,
            background:meta.color, color:'var(--bg)', fontFamily:'monospace' }}>{count}</span>
        )}
        {tiers[id]?.settings?.autoEnableOnOpen && (
          <span title="Auto-enables on market open" style={{ marginLeft:4, fontSize:9, color:meta.color }}>⏰</span>
        )}
      </button>
    );
  };

  return (
    <div style={{ padding:'18px 22px', maxWidth:900, margin:'0 auto', fontFamily:"var(--sans,'Inter',system-ui,sans-serif)" }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase' }}>
            <span style={{ color:'var(--accent)' }}>◈</span> SPY Alert Engine
          </div>
          <div style={{ fontSize:11, color:'var(--faint)', marginTop:2 }}>
            3 tiers · RSI(14) · Bollinger · VWAP confluence
            {engineOn && alertCount>0 && <span style={{ marginLeft:8, color:'var(--warn)' }}>{alertCount} signals this session</span>}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:marketOpen?'var(--up)':'var(--faint)' }}>
            <span style={{ width:6,height:6,borderRadius:'50%',display:'inline-block',
              background:marketOpen?'var(--up)':'var(--faint)',
              animation:marketOpen&&engineOn?'alertPulse 2s infinite':'none' }} />
            {marketOpen?'Market Open':'Market Closed'}
          </div>
          {engineOn && (
            <button onClick={muted?unmute:mute} style={{ ...btnSt,
              borderColor:muted?'var(--warn)':'var(--border)', color:muted?'var(--warn)':'var(--muted)' }}>
              {muted?'🔇 Muted':'🔊 Mute'}
            </button>
          )}
          <button onClick={engineOn?stop:start} style={{ ...btnSt,
            borderColor:engineOn?'var(--up)':'var(--border)',
            color:engineOn?'var(--up)':'var(--muted)',
            background:engineOn?'var(--up-soft)':'var(--bg-hover)', fontWeight:700 }}>
            {engineOn?'⏹ Stop':'▶ Start'}
          </button>
        </div>
      </div>

      {/* Main tab bar */}
      <div style={{ display:'flex', gap:2, background:'var(--bg)', borderRadius:5, padding:2,
        marginBottom:16, border:'1px solid var(--border)', width:'fit-content' }}>
        {tabBtn('overview', 'Overview')}
        {tabBtn('safeday', 'Safe Day')}
        {tabBtn('gameday', 'Game Day')}
        {tabBtn('monitor', 'Monitor')}
        {tabBtn('tiers',   'Tiers')}
        {tabBtn('log',     `Log (${log.length})`)}
      </div>

      {/* ── SAFE DAY (SPY strategy assistant) ──────────────────────────── */}
      {/* ── SAFE DAY — always mounted so alerts fire from any tab ──────── */}
      <div style={{ display: tab==='safeday' ? 'block' : 'none' }}>
        <SafeDayTab
          engineOn={engineOn}
          liveState={liveState}
          marketOpen={marketOpen}
          globalSettings={globalSettings}
          saveGlobalSettings={saveGlobalSettings}
          vixQuote={vixQuote}
        />
      </div>

      {/* ── GAME DAY — always mounted so alerts fire from any tab ───────── */}
      <div style={{ display: tab==='gameday' ? 'block' : 'none' }}>
        <GameDayTab
          engineOn={engineOn}
          symbolStates={symbolStates}
          marketOpen={marketOpen}
          globalSettings={globalSettings}
          saveGlobalSettings={saveGlobalSettings}
          vixQuote={vixQuote}
        />
      </div>

      {/* ── OVERVIEW (simplified per-ticker view) ─────────────────────── */}
      {tab==='overview' && (
        <QuickMonitorTab
          engineOn={engineOn}
          symbolStates={symbolStates}
          watchedSymbols={globalSettings?.watchedSymbols ?? ['SPY']}
          log={log}
          tiers={tiers}
          liveState={liveState}
          vixQuote={vixQuote}
          marketOpen={marketOpen}
        />
      )}

      {/* ── MONITOR ───────────────────────────────────────────────────────── */}
      {tab==='monitor' && (
        <div>
          <>
          {/* Per-symbol status row */}
          {!engineOn && (
            <div style={{ ...sCard, textAlign:'center', fontSize:12, color:'var(--faint)' }}>
              Press <strong style={{ color:'var(--muted)' }}>Start</strong> to begin monitoring.
              Tiers with <strong>Auto-enable on market open</strong> will start automatically at 9:30 ET.
            </div>
          )}
          {engineOn && !liveState && (
            <div style={{ ...sCard, textAlign:'center', fontSize:12, color:'var(--faint)' }}>
              Polling every 60s… first bar loads within a minute.
              {!marketOpen && <span style={{ display:'block', marginTop:4, fontSize:11 }}>Market closed — signals fire during 9:30–4:00 PM ET.</span>}
            </div>
          )}
          {/* Show bar accumulation progress when we have some data but not enough */}
          {engineOn && liveState && liveState.barsNeeded && (
            <div style={{ ...sCard, fontSize:11, color:'var(--faint)', display:'flex', alignItems:'center', gap:8 }}>
              <span>Accumulating bars:</span>
              <div style={{ flex:1, height:4, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
                <div style={{ width:`${Math.round((liveState.barsLoaded/liveState.barsNeeded)*100)}%`,
                  height:'100%', background:'var(--accent)', transition:'width .5s' }} />
              </div>
              <span style={{ fontFamily:'monospace' }}>{liveState.barsLoaded}/{liveState.barsNeeded}</span>
            </div>
          )}
          {/* ── Session Regime + Today's Plan — collapsible accordion ── */}
          {(() => {
            const hasRegime = engineOn && liveState && liveState.bars && liveState.bars.length >= 10;
            const v = hasRegime ? classifySessionRegime(liveState.bars, liveState.adx, liveState.vwap) : null;
            const sizeMult = v ? suggestedSizeMultiplier(v, liveState.minutesIntoSession ?? 0) : null;

            const planOpts = { TRADE: { label: 'Trade it', color: 'var(--up)' }, WAIT_CONFIRM: { label: 'Wait for confirm', color: 'var(--warn)' }, SIT_OUT: { label: 'Sit out', color: 'var(--down)' } };
            const planOpt  = planOpts[gatePlan];

            // Collapsed summary line
            const collapsedContent = (
              <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', flex:1 }}>
                {v ? (
                  <>
                    <span style={{ fontSize:12, fontWeight:800, color:v.color }}>{v.label}</span>
                    <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:3,
                      background:v.color, color:'var(--bg)' }}>{v.action}</span>
                    {sizeMult != null && (
                      <span style={{ fontSize:10, color:'var(--faint)', fontFamily:'monospace' }}>
                        {Math.round(sizeMult*100)}% size
                      </span>
                    )}
                    <span style={{ fontSize:10, color:'var(--border)' }}>·</span>
                  </>
                ) : (
                  <span style={{ fontSize:10, color:'var(--faint)' }}>No live regime yet</span>
                )}
                <span style={{ fontSize:10, color:'var(--faint)' }}>Today:</span>
                {planOpt
                  ? <span style={{ fontSize:11, fontWeight:700, color:planOpt.color }}>{planOpt.label}</span>
                  : <span style={{ fontSize:10, color:'var(--faint)', fontStyle:'italic' }}>no plan set</span>
                }
              </div>
            );

            return (
              <div style={{ border:'1px solid var(--border)', borderRadius:8, marginBottom:16, overflow:'hidden' }}>
                {/* Accordion header */}
                <div onClick={() => setRegimeOpen(o => !o)}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
                    cursor:'pointer', background:'var(--bg-elev, var(--bg))',
                    borderBottom: regimeOpen ? '1px solid var(--border)' : 'none' }}>
                  {collapsedContent}
                  <span style={{ fontSize:10, color:'var(--faint)', flexShrink:0, marginLeft:'auto' }}>
                    {regimeOpen ? '▲' : '▼'}
                  </span>
                </div>

                {/* Expanded body */}
                {regimeOpen && (
                  <div style={{ padding:'14px 14px 6px' }}>
                    {/* Framework advice briefing */}
                    <AdvicePanel liveState={liveState} vixQuote={vixQuote} marketOpen={marketOpen} />
                    {/* Session Regime card */}
                    {v && (() => {
                      const actionBg = v.action==='TRADE' ? 'var(--up-soft)' : v.action==='SIT OUT' ? 'var(--down-soft)' : 'rgba(234,179,8,0.1)';
                      return (
                        <div style={{ ...sCard, border:`1px solid ${v.color}`, background:actionBg, marginBottom:16 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8 }}>
                            <div>
                              <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.1em', color:'var(--faint)', marginBottom:3 }}>
                                Session Regime · SPY {liveState.minutesIntoSession != null && `· ${liveState.minutesIntoSession}min in`}
                              </div>
                              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                <span style={{ fontSize:18, fontWeight:800, color:v.color, letterSpacing:'.02em' }}>{v.label}</span>
                                <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:3,
                                  background:v.color, color:'var(--bg)', letterSpacing:'.05em' }}>{v.action}</span>
                              </div>
                              <div style={{ fontSize:12, color:'var(--text)', marginTop:5, maxWidth:480 }}>{v.verdict}</div>
                            </div>
                            <div style={{ textAlign:'right' }}>
                              <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.08em', color:'var(--faint)' }}>Suggested size</div>
                              <div style={{ fontSize:24, fontWeight:800, color:v.color, fontFamily:'monospace' }}>
                                {Math.round(sizeMult*100)}%
                              </div>
                              <div style={{ fontSize:9, color:'var(--faint)' }}>of full size</div>
                            </div>
                          </div>
                          <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:9, color:'var(--faint)' }}>Confidence</span>
                            <div style={{ flex:1, height:4, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
                              <div style={{ width:`${v.confidence}%`, height:'100%', background:v.color, transition:'width .5s' }} />
                            </div>
                            <span style={{ fontSize:9, fontFamily:'monospace', color:'var(--faint)' }}>{v.confidence}%</span>
                          </div>
                          {v.reasons?.length > 0 && (
                            <div style={{ marginTop:8, display:'flex', flexWrap:'wrap', gap:'3px 10px' }}>
                              {v.reasons.map((r,i)=>(
                                <span key={i} style={{ fontSize:9, color:'var(--muted)', fontFamily:'monospace' }}>· {r}</span>
                              ))}
                            </div>
                          )}
                          <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid var(--border)',
                            display:'flex', flexWrap:'wrap', gap:'4px 16px', fontSize:9, fontFamily:'monospace', color:'var(--faint)' }}>
                            {v.metrics.efficiencyRatio!=null && <span>Efficiency {v.metrics.efficiencyRatio}</span>}
                            {v.metrics.higherHighs!=null && <span>HH {v.metrics.higherHighs} / LL {v.metrics.lowerLows}</span>}
                            {v.metrics.vwapRejections!=null && <span>VWAP crossings {v.metrics.vwapRejections}</span>}
                            {v.metrics.rangeExpansion!=null && <span>Range exp {v.metrics.rangeExpansion}x</span>}
                            {v.metrics.netMovePct!=null && <span>Net move {v.metrics.netMovePct}%</span>}
                          </div>
                        </div>
                      );
                    })()}
                    {/* First Decision Gate */}
                    <FirstDecisionGate
                      engineOn={engineOn}
                      liveState={liveState}
                      marketOpen={marketOpen}
                      symbolStates={symbolStates}
                      watchedSymbols={globalSettings?.watchedSymbols ?? ['SPY']}
                      vixQuote={vixQuote}
                      onPlanChange={setGatePlan}
                    />
                  </div>
                )}
              </div>
            );
          })()}
          {engineOn && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:14 }}>
              {(globalSettings?.watchedSymbols ?? ['SPY']).map(sym => {
                const state  = symbolStates?.[sym];
                const count  = symbolAlertCounts?.[sym] ?? 0;
                const rsi    = state?.rsi;
                const dev    = state?.vwapDev;
                const dir    = state?.confluence?.direction ?? 'neutral';
                const score  = state?.confluence?.score ?? 0;
                const arrow  = dir==='bullish'?'↑':dir==='bearish'?'↓':'→';
                const arrowC = dir==='bullish'?'var(--up)':dir==='bearish'?'var(--down)':'var(--faint)';
                // Build a human-readable reason string for the tooltip
                const reasons = state?.confluence?.factors?.map(f=>f.label).join(', ') ?? '';
                // Bullish = mean-reversion long: RSI oversold OR price below VWAP
                // Bearish = mean-reversion short: RSI overbought OR price above VWAP
                const dirLabel = dir==='bullish' ? 'Bullish (mean-reversion long bias)'
                               : dir==='bearish' ? 'Bearish (mean-reversion short bias)'
                               : 'Neutral';
                return (
                  <div key={sym} title={`${dirLabel}${reasons ? ' · ' + reasons : ''}`} style={{
                    padding:'8px 12px', borderRadius:6, flex:'1 1 140px', minWidth:120,
                    border:`1px solid ${dir==='neutral'?'var(--border)':arrowC}`,
                    background: dir==='neutral'?'var(--bg)':`${arrowC}08`,
                    cursor:'default',
                  }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:12, color:'var(--text)' }}>{sym}</span>
                      <span style={{ fontSize:16, color:arrowC, lineHeight:1 }}>{arrow}</span>
                    </div>
                    <div style={{ fontSize:10, color:'var(--muted)', marginTop:4, fontFamily:'monospace' }}>
                      {rsi!=null
                        ? <span style={{ color: rsi<30?'var(--up)':rsi>70?'var(--down)':'var(--muted)' }}>RSI {rsi.toFixed(1)}</span>
                        : <span>—</span>
                      }
                      {dev!=null
                        ? <span style={{ marginLeft:8,
                            // Below VWAP = bullish (green), above = bearish (red)
                            color: dev<-0.3?'var(--up)':dev>0.3?'var(--down)':'var(--faint)' }}>
                            {dev>=0?'+':''}{dev.toFixed(2)}% VWAP
                          </span>
                        : null}
                    </div>
                    <div style={{ fontSize:9, color:'var(--faint)', marginTop:2, fontFamily:'monospace' }}>
                      {dir!=='neutral' && `score ${score>=0?'+':''}${Math.round(score)}`}
                    </div>
                    {count>0 && <div style={{ fontSize:9, color:'var(--warn)', marginTop:2, fontWeight:700 }}>{count} alert{count!==1?'s':''}</div>}
                    {!state && <div style={{ fontSize:9, color:'var(--faint)', marginTop:2 }}>Polling…</div>}
                    {state && state.barsNeeded && (
                      <div style={{ fontSize:9, color:'var(--faint)', marginTop:2 }}>
                        {state.barsLoaded}/{state.barsNeeded} bars
                      </div>
                    )}
                    {state?.coil?.coiling && (
                      <div style={{ marginTop:4, padding:'3px 6px', borderRadius:3,
                        background:'var(--down-soft)', border:'1px solid var(--down)',
                        fontSize:9, color:'var(--down)', fontWeight:700, letterSpacing:'.03em' }}
                        title={state.coil.verdict}>
                        ⊟ coil {state.coil.daysInBand}d · {state.coil.bandPct}% band — STAND DOWN
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Per-tier status cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
            {TIER_IDS.map(id=>{
              const meta      = TIER_META[id];
              const tierPerst = persist[id]??{};
              const settings  = tiers[id]?.settings??{};
              const threshold = settings.persistBars??3;
              const anyActive = Object.values(tierPerst).some(v=>v>=threshold);
              const count     = tierAlertCounts?.[id]??0;
              const enabled   = settings.enabled!==false;
              return (
                <div key={id} style={{
                  padding:'12px 14px', borderRadius:7, opacity:enabled?1:0.4, transition:'all .3s',
                  border:`1px solid ${anyActive?meta.color:'var(--border)'}`,
                  background:anyActive?`${meta.color}10`:'var(--bg)',
                  boxShadow:anyActive?`0 0 10px ${meta.color}30`:'none',
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:anyActive?meta.color:'var(--muted)', letterSpacing:'.06em' }}>
                        {meta.name.toUpperCase()}
                      </div>
                      <div style={{ fontSize:9, color:'var(--faint)', marginTop:2 }}>{settings.minConfluence??1} signal{(settings.minConfluence??1)!==1?'s':''} req.</div>
                    </div>
                    <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                      {settings.autoEnableOnOpen && <span title="Auto-enables on open" style={{ fontSize:10, color:'var(--faint)' }}>⏰</span>}
                      {count>0 && (
                        <span style={{ fontSize:9, fontFamily:'monospace', padding:'1px 5px', borderRadius:8,
                          background:`${meta.color}20`, color:meta.color }}>{count}</span>
                      )}
                    </div>
                  </div>
                  {/* Persist pip rows — one per signal type */}
                  {[
                    { key:'oversold',   label:'Oversold ↘',  },
                    { key:'overbought', label:'Overbought ↗', },
                    { key:'bbLower',    label:'BB lower ⤓',   },
                    { key:'bbUpper',    label:'BB upper ⤒',   },
                  ].map(({key, label})=>{
                    const c = tierPerst[key]??0;
                    return (
                      <div key={key} style={{ display:'flex', alignItems:'center', gap:5, marginBottom:4 }}>
                        <span style={{ fontSize:9, color:'var(--faint)', width:76, whiteSpace:'nowrap' }}>{label}</span>
                        <div style={{ display:'flex', gap:2 }}>
                          {Array.from({length:Math.max(threshold,Math.min(c,threshold+2))}).map((_,i)=>(
                            <div key={i} style={{
                              width:8, height:8, borderRadius:2, transition:'background .3s',
                              background:i<c?meta.color:'var(--bg)',
                              border:`1px solid ${i===threshold-1?meta.color:'var(--border)'}`,
                            }} />
                          ))}
                        </div>
                        <span style={{ fontSize:9, fontFamily:'monospace', color:c>=threshold?meta.color:'var(--faint)' }}>{c}</span>
                      </div>
                    );
                  })}
                  {anyActive && <div style={{ marginTop:6, fontSize:9, fontWeight:700, letterSpacing:'.1em', color:meta.color }}>● ACTIVE</div>}
                </div>
              );
            })}
          </div>


          {/* ── Volatility & Regime ──────────────────────────────────── */}
          <VolatilityPanel
            symbolStates={symbolStates}
            watchedSymbols={globalSettings?.watchedSymbols ?? ['SPY']}
          />
          {/* Per-symbol indicators — collapsible accordion per symbol */}
          {Object.entries(symbolStates ?? {}).some(([,s])=>s!=null) && (
            <div style={{ ...sCard, padding:0, overflow:'hidden' }}>
              {(globalSettings?.watchedSymbols ?? ['SPY']).map((sym, idx, arr) => {
                const s = symbolStates?.[sym];
                if (!s) return null;
                const isOpen = !!expandedSyms[sym];
                const dir    = s.confluence?.direction ?? 'neutral';
                const arrow  = dir==='bullish' ? '↑' : dir==='bearish' ? '↓' : '→';
                const arrowC = dir==='bullish' ? 'var(--up)' : dir==='bearish' ? 'var(--down)' : 'var(--faint)';
                const rsi    = s.rsi;
                const dev    = s.vwapDev;
                const regimeLabel = dir==='bullish' ? 'Bullish' : dir==='bearish' ? 'Bearish' : 'Neutral';
                const adxLabel = s.adx == null ? null
                  : s.adx >= 30 ? 'Strong trend' : s.adx >= 20 ? 'Trending'
                  : s.adx >= 14 ? 'Weak trend'   : 'Ranging';
                const rsiLabel = rsi == null ? null
                  : rsi < 30 ? 'Oversold' : rsi > 70 ? 'Overbought'
                  : rsi < 45 ? 'Low'      : rsi > 55 ? 'High' : 'Neutral';
                const isLast = idx === arr.length - 1;
                return (
                  <div key={sym} style={{ borderBottom: isLast ? 'none' : '1px solid var(--border)' }}>
                    {/* Collapsed header row */}
                    <div onClick={() => setExpandedSyms(p => ({ ...p, [sym]: !p[sym] }))}
                      style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px',
                        cursor:'pointer', background: isOpen ? 'var(--bg-hover)' : 'transparent',
                        transition:'background .15s' }}>
                      <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:12, color:'var(--text)', minWidth:40 }}>{sym}</span>
                      <span style={{ fontSize:15, color:arrowC, lineHeight:1 }}>{arrow}</span>
                      <span style={{ fontSize:11, fontWeight:600, color:arrowC }}>{regimeLabel}</span>
                      {adxLabel && <span style={{ fontSize:10, color:'var(--faint)', fontFamily:'monospace' }}>· {adxLabel}</span>}
                      {rsi != null && (
                        <span style={{ fontSize:10, fontFamily:'monospace', marginLeft:'auto',
                          color: rsi<30?'var(--up)':rsi>70?'var(--down)':'var(--muted)' }}>
                          RSI {rsi.toFixed(0)}
                          {rsiLabel && <span style={{ fontSize:9, color:'var(--faint)', marginLeft:4 }}>({rsiLabel})</span>}
                        </span>
                      )}
                      {s.coil?.coiling && (
                        <span style={{ fontSize:9, color:'var(--down)', fontWeight:700, padding:'1px 5px',
                          border:'1px solid var(--down)', borderRadius:3, whiteSpace:'nowrap' }}>⊟ coil</span>
                      )}
                      <span style={{ fontSize:9, color:'var(--faint)', whiteSpace:'nowrap', marginLeft: rsi==null?'auto':0 }}>
                        {s.price!=null ? `$${s.price.toFixed(2)} ` : ''}{isOpen ? '▲' : '▼'}
                      </span>
                    </div>
                    {/* Expanded detail */}
                    {isOpen && (
                      <div style={{ padding:'10px 14px 12px', borderTop:'1px solid var(--border)', background:'var(--bg)' }}>
                        <div style={{ fontSize:9, color:'var(--faint)', fontFamily:'monospace', marginBottom:8, textAlign:'right' }}>
                          {new Date(s.timestamp).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false})}
                        </div>
                        {rsi!=null && (
                          <div style={{ marginBottom:10 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--muted)', marginBottom:3 }}>
                              <span>RSI (14)</span>
                              <span style={{ fontFamily:'monospace', fontWeight:700,
                                color:rsi<30?'var(--up)':rsi>70?'var(--down)':'var(--text)' }}>
                                {rsi.toFixed(1)}
                                <span style={{ fontWeight:400, fontSize:9, marginLeft:4, color:'var(--faint)' }}>
                                  {rsi<30?'oversold →bullish':rsi>70?'overbought →bearish':'neutral'}
                                </span>
                              </span>
                            </div>
                            <div style={{ height:5, background:'var(--bg)', borderRadius:3, border:'1px solid var(--border)', overflow:'hidden', position:'relative' }}>
                              <div style={{ position:'absolute', left:'30%', width:1, height:'100%', background:'var(--up)', opacity:.4 }} />
                              <div style={{ position:'absolute', left:'70%', width:1, height:'100%', background:'var(--down)', opacity:.4 }} />
                              <div style={{ width:`${rsi}%`, height:'100%', borderRadius:3, transition:'width .5s, background .3s',
                                background:rsi<30?'var(--up)':rsi>70?'var(--down)':'var(--border)' }} />
                            </div>
                          </div>
                        )}
                        {dev!=null && (
                          <div style={{ fontSize:11, color:'var(--muted)', display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                            <span>vs VWAP</span>
                            <span style={{ fontFamily:'monospace', fontWeight:700,
                              color:dev<-0.3?'var(--up)':dev>0.3?'var(--down)':'var(--text)' }}>
                              {dev>=0?'+':''}{dev.toFixed(3)}%
                              <span style={{ fontWeight:400, fontSize:9, marginLeft:4, color:'var(--faint)' }}>
                                {dev<-0.3?'below →bullish':dev>0.3?'above →bearish':'near VWAP'}
                              </span>
                            </span>
                          </div>
                        )}
                        {s.adx != null && (
                          <div style={{ fontSize:11, color:'var(--muted)', display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                            <span>ADX</span>
                            <span style={{ fontFamily:'monospace', fontWeight:700, color:'var(--text)' }}>
                              {s.adx.toFixed(1)}
                              <span style={{ fontWeight:400, fontSize:9, marginLeft:4, color:'var(--faint)' }}>{adxLabel}</span>
                            </span>
                          </div>
                        )}
                        {s.coil?.coiling && (
                          <div style={{ marginTop:4, padding:'6px 10px', borderRadius:4,
                            background:'var(--down-soft)', border:'1px solid var(--down)',
                            fontSize:10, color:'var(--down)', fontWeight:700 }}>
                            ⊟ {s.coil.daysInBand}-day coil · ${s.coil.bandLow}–${s.coil.bandHigh} · {s.coil.bandPct}% band — STAND DOWN
                          </div>
                        )}
                        {s.confluence?.factors?.length > 0 && (
                          <div style={{ marginTop:8, display:'flex', flexWrap:'wrap', gap:'3px 10px' }}>
                            {s.confluence.factors.map((f,i) => (
                              <span key={i} style={{ fontSize:9, color:'var(--muted)', fontFamily:'monospace' }}>· {f.label}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Recent alerts */}
          {log.length>0 && (
            <div>
              <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'.08em', color:'var(--faint)', marginBottom:8 }}>Recent</div>
              {log.slice(0,5).map(e=><LogRow key={e.id} entry={e} onDelete={deleteLogEntry} tiers={tiers} />)}
              {log.length>5 && <button onClick={()=>setTab('log')} style={{ background:'none',border:'none',cursor:'pointer',color:'var(--muted)',fontSize:11,padding:'4px 0' }}>View all {log.length} →</button>}
            </div>
          )}
          </>
        </div>
      )}

      {/* ── TIERS ─────────────────────────────────────────────────────────── */}
      {tab==='tiers' && (
        <div>
          {/* Tier + Global sub-tab bar */}
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16 }}>
            {TIER_IDS.map(id=>tierTabBtn(id))}
            <button onClick={()=>setTierTab('global')} style={{
              padding:'5px 12px', border:`1px solid ${tierTab==='global'?'var(--faint)':'var(--border)'}`,
              cursor:'pointer', fontSize:11, fontWeight:700, letterSpacing:'.05em',
              background:tierTab==='global'?'var(--bg-hover)':'transparent',
              color:tierTab==='global'?'var(--text)':'var(--muted)',
              borderRadius:4, transition:'all .15s',
            }}>⚙ Global</button>
          </div>

          {/* Tier description */}
          {tierTab!=='global' && (
            <div style={{ fontSize:11, color:'var(--faint)', marginBottom:14, padding:'8px 12px',
              borderRadius:5, background:'var(--bg)', border:'1px solid var(--border)' }}>
              <strong style={{ color:TIER_META[tierTab].color }}>{TIER_META[tierTab].name}</strong>
              {' — '}{TIER_META[tierTab].description}
            </div>
          )}

          {/* Render active pane */}
          {TIER_IDS.map(id=>tierTab===id && (
            <TierSettingsPane key={id} tierId={id}
              tierState={tiers[id]} globalSettings={globalSettings}
              onSave={saveTierSettings} onSetRingtone={setTierRingtone} />
          ))}
          {tierTab==='global' && (
            <GlobalPane globalSettings={globalSettings} onSave={saveGlobalSettings} />
          )}
        </div>
      )}

      {/* ── LOG ───────────────────────────────────────────────────────────── */}
      {tab==='log' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <span style={{ fontSize:12, color:'var(--muted)' }}>{log.length} alert{log.length!==1?'s':''} recorded</span>
            {log.length>0 && <button onClick={clearLog} style={{ ...btnSt, color:'var(--down)' }}>Clear all</button>}
          </div>
          {log.length===0 ? (
            <div style={{ textAlign:'center', color:'var(--faint)', fontSize:12, padding:'40px 20px' }}>
              No alerts yet. Start the engine during market hours.
            </div>
          ) : (
            <>
              {/* Stats grid */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:16 }}>
                {TIER_IDS.map(id=>{
                  const meta  = TIER_META[id];
                  const count = log.filter(e=>e.tierId===id).length;
                  const bull  = log.filter(e=>e.tierId===id&&e.severity==='bullish').length;
                  const bear  = log.filter(e=>e.tierId===id&&e.severity==='bearish').length;
                  return (
                    <div key={id} style={{ padding:'10px 12px', borderRadius:5, border:`1px solid ${count>0?meta.color:'var(--border)'}`,
                      background:count>0?`${meta.color}08`:'var(--bg-hover)' }}>
                      <div style={{ fontSize:9, textTransform:'uppercase', letterSpacing:'.06em', color:meta.color, marginBottom:3 }}>{meta.name}</div>
                      <div style={{ fontSize:22, fontWeight:700, color:meta.color }}>{count}</div>
                      {count>0 && <div style={{ fontSize:9, color:'var(--faint)', marginTop:2 }}>▲{bull} ▼{bear}</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{ maxHeight:500, overflowY:'auto' }}>
                {log.map(e=><LogRow key={e.id} entry={e} onDelete={deleteLogEntry} tiers={tiers} />)}
              </div>
            </>
          )}
        </div>
      )}

      <style>{`@keyframes alertPulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
    </div>
  );
}
